import * as React from 'react';
import { atLeast, endOfMonth, startOfMonth, type Role } from '@cfl/shared';

import { Button, Combobox, Input, Label } from '@/components/cargofl-ui';
import { useCustomers, useProjects } from '@/lib/catalog-api';
import { useVisiblePeople } from '@/lib/people-api';
import type { Filters } from '@/lib/reporting-api';
import { orgToday } from '@/lib/today';

/**
 * The filter row shared by every view of recorded time.
 *
 * One component, because "All times", the export and the reports all filter the
 * same data - and a filter that behaves differently between two screens showing the
 * same numbers is how people stop trusting either.
 *
 * Pickers are comboboxes: 60 projects and 77 people are a search, not a dropdown.
 */
export function FilterBar({
  value,
  onChange,
  role,
  children,
}: {
  value: Filters;
  onChange: (next: Filters) => void;
  role: Role;
  /** Actions that belong beside the filters - a download button, a page size. */
  children?: React.ReactNode;
}) {
  const canSeeOthers = atLeast(role, 'SUPERVISOR');
  // Only asked for when the picker is actually shown - an employee's query would
  // be refused, and the control is hidden for them anyway.
  const { data: people } = useVisiblePeople(canSeeOthers);
  const { data: customers } = useCustomers();
  const { data: projects } = useProjects();

  const set = (patch: Partial<Filters>) => onChange({ ...value, ...patch });
  const one = (ids?: string[]) => ids?.[0] ?? null;

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
      <div className="space-y-1.5">
        <Label htmlFor="f-from" className="text-xs">
          From
        </Label>
        <Input
          id="f-from"
          type="date"
          className="h-9 w-[150px]"
          value={value.from ?? ''}
          onChange={(e) => set({ from: e.target.value || undefined })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="f-to" className="text-xs">
          To
        </Label>
        <Input
          id="f-to"
          type="date"
          className="h-9 w-[150px]"
          value={value.to ?? ''}
          onChange={(e) => set({ to: e.target.value || undefined })}
        />
      </div>

      {/* Hidden entirely for an employee: the API narrows their query to themselves
          anyway, so offering the control would only produce a filter that does
          nothing and look broken. */}
      {canSeeOthers && (
        <div className="w-[190px] space-y-1.5">
          <Label className="text-xs">Person</Label>
          <Combobox
            value={one(value.employeeIds)}
            onChange={(v) => set({ employeeIds: v ? [v] : undefined })}
            options={(people ?? []).map((p) => ({ value: p.id, label: p.displayName }))}
            placeholder="Everyone"
            searchPlaceholder="Search people"
          />
        </div>
      )}

      <div className="w-[190px] space-y-1.5">
        <Label className="text-xs">Customer</Label>
        <Combobox
          value={one(value.customerIds)}
          onChange={(v) =>
            // Clearing the customer must clear a project that belonged to it, or the
            // filter reads as one customer while querying another's project.
            set({ customerIds: v ? [v] : undefined, projectIds: undefined })
          }
          options={(customers ?? []).map((c) => ({ value: c.id, label: c.name }))}
          placeholder="All customers"
          searchPlaceholder="Search customers"
        />
      </div>

      <div className="w-[190px] space-y-1.5">
        <Label className="text-xs">Project</Label>
        <Combobox
          value={one(value.projectIds)}
          onChange={(v) => set({ projectIds: v ? [v] : undefined })}
          options={(projects ?? [])
            // Narrowed to the chosen customer, so the list agrees with the rest of
            // the filter.
            .filter((p) => !value.customerIds?.length || value.customerIds.includes(p.customer.id))
            .map((p) => ({ value: p.id, label: p.name, description: p.customer.name }))}
          placeholder="All projects"
          searchPlaceholder="Search projects"
        />
      </div>

      <div className="flex-1" />
      <div className="flex items-end gap-2">
        {children}
        {/* Clears the filters but keeps the date range: starting again almost never
            means "and show me all of history". */}
        <Button hint={"Clear all filters"} variant="ghost" onClick={() => onChange({ from: value.from, to: value.to })}>
          Clear
        </Button>
      </div>
    </div>
  );
}

/** The month containing today, as a starting filter. */
export function currentMonthFilter(): Filters {
  const today = orgToday();
  return { from: startOfMonth(today), to: endOfMonth(today) };
}
